import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxArchive, faRotateRight, faTag } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import SortableTableHeader from '../../../common/SortableTableHeader/SortableTableHeader';
import PaginatedDeviceListNavigation from './PaginatedDeviceListNavigation';
import {
  fetchFilteredDeviceList,
  refreshFilteredDeviceList, selectAllIds,
  selectDeviceListActiveSearchParams, selectDeviceListColumnVisibility,
  selectFilteredDeviceList,
} from '../../../../redux/slices/filteredDeviceListSlice/filteredDeviceListSlice';
import DeviceListTableRow from './DeviceListTableRow/DeviceListTableRow';
import { selectTagList } from '../../../../redux/slices/tagListSlice/tagListSlice';
import useSelection from '../../../hooks/useSelection/useSelection';
import ApplyTagsModal from '../../../guidance/tags/modals/ApplyTagsModal/ApplyTagsModal';
import EntityType from '../../../../utility/EntityType';
import BulkArchiveDeviceModal from '../BulkArchiveDevicesModal/BulkArchiveDeviceModal';
import IndeterminateCheckbox from '../../../common/IndeterminateCheckbox/IndeterminateCheckbox';
import {
  connectModalContainerContextToComponent,
} from '../../../../contexts/ModalContext/ModalContext';
import ListSortType from '../../../../utility/types/listSortType';
import { RaaRoles } from '../../../../utility/SecurityRules';
import HasRolePrivileges from '../../../highorder/HasRolePrivileges';
import ListSelectionBanner from '../../../common/RaaLists/ListSelectionBanner/ListSelectionBanner';
import DeviceListSuspendedTableRow from './DeviceListTableRow/DeviceListSuspendedTableRow';
import TagUtility from '../../../../utility/TagUtility';

const columnLabelMap = [
  { label: 'Push Status', sortType: ListSortType.PUSH_STATUS },
  { label: 'Device Name', sortType: ListSortType.DEVICE_NAME },
  { label: 'Tags', sortType: ListSortType.TAG },
  { label: 'Model', sortType: ListSortType.MODEL },
  { label: 'MAC Address', sortType: ListSortType.MAC_ADDRESS },
  { label: 'Last Message', sortType: ListSortType.LAST_MESSAGE },
  { label: 'Room Alert Link', sortType: ListSortType.LINK_STATUS },
  { label: 'Firmware', sortType: ListSortType.FIRMWARE },
];

/* istanbul ignore next */
function DeviceListTable({ setModalJSX }) {
  const dispatch = useDispatch();

  const deviceList = useSelector(selectFilteredDeviceList);
  const tags = useSelector(selectTagList);
  const lastSearchParams = useSelector(selectDeviceListActiveSearchParams);
  const deviceListColumnVisibility = useSelector(selectDeviceListColumnVisibility);
  const allDeviceIds = useSelector(selectAllIds);
  // eslint-disable-next-line max-len
  const selectedSortType = lastSearchParams.sortType;
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const handleGetSortedList = (sort, sortType) => dispatch(
    fetchFilteredDeviceList({ ...lastSearchParams, sort, sortType }),
  );

  const handleRefreshFilteredList = () => dispatch(refreshFilteredDeviceList());

  const {
    handleSelectAllOnPage,
    isAllSelected,
    isIndeterminate,
    isSelected,
    handleSelect,
    selected,
    clearSelected,
    handleSelectEntireList,
    getSelectedObjectsFromList,
  } = useSelection();

  const handleSetChecked = () => {
    if (isIndeterminate()) {
      return 'indeterminate';
    }
    if (isAllSelected(deviceList)) {
      return 'true';
    }
    return 'false';
  };

  const handleOpenApplyTagsModal = () => {
    const selectedDevices = getSelectedObjectsFromList(deviceList);
    const tagIds = tags.map((tag) => tag.id);
    const { tagsAssociatedWithAllSelected, tagsAssociatedWithSomeSelected } = TagUtility
      .getTagAssociationsForSelectedDevices(selectedDevices, tagIds);
    setModalJSX((
      <ApplyTagsModal
        selectedIdList={selected}
        entityType={EntityType.DEVICE}
        tagsAssociatedWithAll={tagsAssociatedWithAllSelected}
        tagsAssociatedWithSome={tagsAssociatedWithSomeSelected}
      />
    ));
  };

  const handleBulkArchive = () => {
    setModalJSX((<BulkArchiveDeviceModal
      deviceIdList={selected}
      onClose={() => setModalJSX((<></>))}
      onArchive={() => handleSelectAllOnPage([])}
    />));
  };

  return (
    <>
      <div className="clearfix pr-2 pt-2 pb-2">
        <div className="d-flex float-left">
          <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
            <>
              <IndeterminateCheckbox
                onChange={() => handleSelectAllOnPage(deviceList)}
                checked={handleSetChecked()}
              />
              {
            selected.length > 0 && (
              <>
                <Button variant="link" onClick={handleOpenApplyTagsModal}>
                  <FontAwesomeIcon
                    icon={faTag}
                    size="lg"
                    className="text-muted"
                    title="Tag"
                  />
                </Button>
                {' '}
                <Button variant="link" onClick={handleBulkArchive}>
                  <FontAwesomeIcon
                    icon={faBoxArchive}
                    size="lg"
                    className="text-muted"
                    title="Archive"
                  />
                </Button>
              </>
            )
          }
            </>
          </HasRolePrivileges>
          <Button variant="link" onClick={handleRefreshFilteredList}>
            <FontAwesomeIcon
              icon={faRotateRight}
              size="lg"
              className="text-muted"
            />
          </Button>
        </div>
        <div className="float-right">
          <PaginatedDeviceListNavigation />
        </div>
      </div>
      <ListSelectionBanner
        allEntityIds={allDeviceIds}
        isAllSelected={isAllSelected(allDeviceIds)}
        /* eslint-disable-next-line max-len */
        isAllPageOnlySelected={isAllSelected(deviceList) && !isAllSelected(allDeviceIds)}
        selected={selected}
        clearSelected={clearSelected}
        handleSelectAll={() => handleSelectEntireList(allDeviceIds)}
      />
      <table className="table table-sm table-condensed table-striped my-1">
        <thead className="thead-light">
          <tr>
            <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
              <th>{' '}</th>
            </HasRolePrivileges>
            {
            columnLabelMap.map((col) => {
              if (deviceListColumnVisibility[col.label] === true) {
                return (
                  <React.Fragment key={col.label}>
                    <SortableTableHeader
                      onDescClick={() => handleGetSortedList('DES', col.sortType)}
                      label={col.label}
                      onAscClick={() => handleGetSortedList('ASC', col.sortType)}
                      isSelected={col.sortType === selectedSortType}
                    />
                  </React.Fragment>
                );
              }
              return (<></>);
            })
          }
          </tr>
        </thead>
        <tbody>
          {deviceList.map((device, index) => (
            <React.Fragment key={index}>
              {device.suspended
                ? (
                  <DeviceListSuspendedTableRow
                    hoveredIndex={hoveredIndex}
                    index={index}
                    setHoveredIndex={setHoveredIndex}
                    device={device}
                    isOnLatestFirmware
                    isSelected={isSelected(device.id)}
                    handleSetSelected={() => handleSelect(device.id)}
                  />
                )
                : (
                  <DeviceListTableRow
                    hoveredIndex={hoveredIndex}
                    index={index}
                    setHoveredIndex={setHoveredIndex}
                    isOnLatestFirmware
                    device={device}
                    isSelected={isSelected(device.id)}
                    handleSetSelected={() => handleSelect(device.id)}
                    disableQuickActions={isSelected(device.id)}
                  />
                )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
}

DeviceListTable.propTypes = {
  setModalJSX: PropTypes.func.isRequired,
};

export default connectModalContainerContextToComponent(DeviceListTable);
